// Enums
import QuestionFormat from 'engagement/enums/QuestionFormat';

// Utilities
import { createNumericTableAndCsvData } from 'admin/utils/helpers';

const transformBasicData = (data) =>
  data.map(({ responseId, answer, createdAt }) => ({
    id: responseId,
    value: answer,
    createdAt,
  }));

const transformSingleData = (data) =>
  data.map(({ answer, answerCount, percentage }) => ({
    label: answer,
    count: answerCount,
    percentage: Number(percentage),
  }));

const transformOrderedData = (data) => ({
  options: data.options.map(({ inputId, optionLabel, weight }) => ({
    id: inputId,
    label: optionLabel,
    score: weight,
  })),
  values: data.values.map(({ rank, inputId, answerCount, percentage }) => ({
    optionId: inputId,
    count: answerCount,
    rank: Number(rank),
    percentage: Number(percentage),
  })),
});

const transformMatrixdata = (data) => {
  const weights = data.options.map(({ id, weightLabel }) => ({ label: weightLabel, id }));
  const optionsMap = data.values.reduce((map, { answer, answerCount, weightId, percentage }) => {
    if (!map[answer]) map[answer] = []; // eslint-disable-line no-param-reassign
    map[answer].push({ weightId, count: answerCount, percentage: Number(percentage) });

    return map;
  }, {});
  const options = Object.entries(optionsMap).map(([label, values]) => ({ label, values }));

  return { weights, options };
};

const transformNumericData = (data, displayData) => {
  const { rangeStart, rangeEnd, decimalDigits } = displayData;
  const numericData = data.map(({ answer }) => Number(answer));
  const { sanitizedData } = createNumericTableAndCsvData(
    numericData,
    rangeStart,
    rangeEnd,
    decimalDigits
  );

  return sanitizedData;
};

const transformRatingData = (data, displayData) => {
  const { scale } = displayData;
  const numericData = data.map(({ answer }) => Number(answer));
  const resultData = [];

  for (let index = 1; index <= Number(scale); index += 1) {
    const label = String(index);
    const count = numericData.filter((value) => value === index).length;
    const percentage = (count / numericData.length) * 100;

    resultData.push({ label, count, percentage });
  }

  return resultData;
};

const transformQuestionData = ({ format, data, displayData }) => {
  switch (format) {
    case QuestionFormat.BASIC:
      return transformBasicData(data);
    case QuestionFormat.SINGLE_SELECT:
    case QuestionFormat.MULTI_SELECT:
    case QuestionFormat.BUDGET:
    case QuestionFormat.DROPDOWN:
      return transformSingleData(data);
    case QuestionFormat.ORDERED:
      return transformOrderedData(data);
    case QuestionFormat.MATRIX:
      return transformMatrixdata(data);
    case QuestionFormat.NUMERIC:
    case QuestionFormat.SLIDER:
      return transformNumericData(data, displayData);
    case QuestionFormat.RATING:
      return transformRatingData(data, displayData);
    default:
      return data;
  }
};

export const transformBackendData = (engagementReport) => {
  for (const section of engagementReport.snapshot.sections) {
    for (const question of section.questions) {
      question.data = transformQuestionData(question);
    }
  }

  return engagementReport;
};
